/**
 * Locale resolution for the generated product-detail pages.
 *
 * WHY THIS EXISTS
 * ---------------
 * `cms_product_pages` stores every text field three times (`_zhHant` /
 * `_zhHans` / `_en`) and stores the page BODY as a JSON tree per locale
 * (`blocks_zhHant` / `blocks_zhHans` / `blocks_en`). The build-time sync
 * used to fetch each page once, with `?locale=zh-Hant`, so the generated
 * `product-pages.generated.ts` carried Chinese copy only:
 * `/en/products/values-incubator` rendered the Chinese body even though
 * the page is a different page per locale.
 *
 * The sync now fetches all three locales and merges them into
 * `entry.locales`. This module is the front-end half of that: it picks the
 * right locale's copy out of a generated entry.
 *
 * WHERE THE COPY COMES FROM
 * -------------------------
 * The public endpoint resolves each locale on the wire: `zh-Hans` falls
 * back to the Traditional body converted leaf by leaf, and `en` falls
 * back to the copy compiled in `server/lib/product-page-en-copy.ts`
 * (the English that shipped before the CMS migration). The sync repeats
 * both fallbacks, so the snapshot does not depend on which API the build
 * happened to read.
 *
 * THE FALLBACK RULES (in order)
 * ------------------------------
 * 1. The requested locale's own copy, when it has a body of its own.
 * 2. Otherwise the zh-Hant copy, WHOLE — not field by field. A locale
 *    whose `blocks` are empty is a locale whose copy has not been written
 *    yet, so mixing an English scalar with a Chinese body would be worse
 *    than showing one language.
 * 3. When the locale's copy has no entry at all, the flat fields of the
 *    row — which the sync keeps mirroring zh-Hant.
 * 4. A generated file written by an older sync has no `locales` map at
 *    all; the flat fields are then the zh-Hant copy, and every locale
 *    renders it. A degraded but correct Chinese page, never a broken
 *    build.
 *
 * DEFENSIVE ABOUT A STALE GENERATED FILE
 * --------------------------------------
 * `ProductPageLocaleCopy` is declared here rather than imported from the
 * generated module, and `locales` is read through a cast. A generated file
 * written by an older sync (or by a watcher still running the old code)
 * has no `locales` key at all — the page then falls back to the flat
 * zh-Hant fields rather than failing to compile.
 */

import { cmsProductPages, type CmsProductPageIndexEntry } from './product-pages.generated'
import { DEFAULT_LOCALE, type Locale } from '@/utils/localePath'
import { previewTokenParam } from '@/utils/previewToken'

/** One locale's copy of a product page — the chrome scalars plus the body. */
export interface ProductPageLocaleCopy {
  subtitle: string
  summary: string
  hero_alt: string
  /** The page body tree. Empty when the locale has no copy of its own. */
  blocks: Record<string, unknown>
  cta: {
    tag: string
    title: string
    text: string
    whatsapp_label: string
    form_label: string
    closing: string
  }
}

/** A generated row with one locale's copy resolved onto its flat fields. */
export type LocalizedProductPage = Omit<CmsProductPageIndexEntry, 'locales'> &
  ProductPageLocaleCopy

/** The `locales` map, as the locale-aware sync writes it. */
type ProductPageLocales = Partial<Record<Locale, ProductPageLocaleCopy>>

/** The i18next language tag → the site's locale. */
export function localeOf(language: string | undefined): Locale {
  if (language === 'zh-Hans' || language === 'zh-CN') return 'zh-Hans'
  if (language === 'en') return 'en'
  return DEFAULT_LOCALE
}

/** The copy carried by one row's flat fields (the zh-Hant mirror). */
function flatCopy(page: CmsProductPageIndexEntry): ProductPageLocaleCopy {
  return {
    subtitle: page.subtitle ?? '',
    summary: page.summary ?? '',
    hero_alt: page.hero_alt ?? '',
    blocks: page.blocks ?? {},
    cta: {
      tag: page.cta?.tag ?? '',
      title: page.cta?.title ?? '',
      text: page.cta?.text ?? '',
      whatsapp_label: page.cta?.whatsapp_label ?? '',
      form_label: page.cta?.form_label ?? '',
      closing: page.cta?.closing ?? '',
    },
  }
}

/** A body tree with nothing in it — the signal that a locale has no copy. */
function isEmptyBody(blocks: Record<string, unknown> | undefined): boolean {
  return !blocks || Object.keys(blocks).length === 0
}

/** Field-by-field merge, so a partial copy never leaves a hole. */
function mergeCopy(
  want: ProductPageLocaleCopy,
  base: ProductPageLocaleCopy,
): ProductPageLocaleCopy {
  return {
    subtitle: want.subtitle || base.subtitle,
    summary: want.summary || base.summary,
    hero_alt: want.hero_alt || base.hero_alt,
    blocks: isEmptyBody(want.blocks) ? base.blocks : want.blocks,
    cta: {
      tag: want.cta.tag || base.cta.tag,
      title: want.cta.title || base.cta.title,
      text: want.cta.text || base.cta.text,
      whatsapp_label: want.cta.whatsapp_label || base.cta.whatsapp_label,
      form_label: want.cta.form_label || base.cta.form_label,
      closing: want.cta.closing || base.cta.closing,
    },
  }
}

/**
 * The copy for one locale of a product-detail page.
 *
 * Falls back to zh-Hant when the requested locale has no copy of its own —
 * the generated file always carries all three, but a file written by an
 * older sync carries none, and the flat fields are the zh-Hant copy.
 */
export function productPageCopy(
  page: CmsProductPageIndexEntry,
  locale: Locale,
): ProductPageLocaleCopy {
  const locales = (page as { locales?: ProductPageLocales }).locales
  const base = locales?.[DEFAULT_LOCALE] ?? flatCopy(page)
  const want = locales?.[locale]
  if (!want) return base

  // An empty body means "this locale has no copy yet" — not "this locale
  // has a short page". The server and the sync both fill the gap for
  // `zh-Hans` and `en`, so an empty body here means the snapshot is older
  // than that, or the row's copy has drifted away from the copy the
  // server knows. Fall back to zh-Hant whole, rather than pairing one
  // language's chrome with another language's body.
  if (isEmptyBody(want.blocks) && !isEmptyBody(base.blocks)) return base

  return mergeCopy(want, base)
}

/** One generated row, resolved for `locale`. */
export function localizeProductPage(
  page: CmsProductPageIndexEntry,
  locale: Locale,
): LocalizedProductPage {
  const { locales: _locales, ...rest } = page as CmsProductPageIndexEntry & {
    locales?: ProductPageLocales
  }
  return { ...rest, ...productPageCopy(page, locale) }
}

/** The whole generated index, resolved for `locale`. */
export function localizeProductPages(
  pages: CmsProductPageIndexEntry[],
  locale: Locale,
): LocalizedProductPage[] {
  return pages.map((p) => localizeProductPage(p, locale))
}

/** The whole generated index, resolved for the active locale. */
export function localizedProductPages(locale: Locale): LocalizedProductPage[] {
  return localizeProductPages(cmsProductPages, locale)
}

/**
 * Fetch one product page from the live CMS, for the active locale.
 *
 * Used by the detail pages when the build-time snapshot has no row for the
 * slug (cold cache, or a draft preview). The public endpoint resolves each
 * locale on the wire — a Chinese locale falls back to the opposite variant
 * converted, English falls back to the compiled-in copy — so the response
 * already carries the right language. The zh-Hant copy is fetched
 * alongside it and merged into the same `locales` map the generated
 * snapshot carries, so `productPageCopy` falls back exactly as it does for
 * the snapshot.
 *
 * The returned row keeps the flat fields of the ACTIVE locale (the shape
 * the live endpoint serves) and carries the merged `locales` map on top.
 */
export async function fetchLiveProductPage(
  slug: string,
  locale: Locale,
): Promise<CmsProductPageIndexEntry | null> {
  const token = previewTokenParam()
  const load = async (loc: Locale): Promise<CmsProductPageIndexEntry | null> => {
    const r = await fetch(`/api/cms/public/product-pages/${slug}?locale=${loc}${token}`)
    if (!r.ok) return null
    const j = (await r.json()) as { product_page?: CmsProductPageIndexEntry }
    return j.product_page ?? null
  }

  try {
    const active = await load(locale)
    if (!active) return null

    const locales: ProductPageLocales = { [locale]: flatCopy(active) }
    if (locale !== DEFAULT_LOCALE && isEmptyBody(active.blocks)) {
      const base = await load(DEFAULT_LOCALE)
      if (base) locales[DEFAULT_LOCALE] = flatCopy(base)
    }

    return { ...active, locales } as CmsProductPageIndexEntry
  } catch {
    // A network failure is not a missing page — the caller treats a null as
    // "no copy", exactly as the previous page-local fetch did.
    return null
  }
}
